import { useMemo, useState } from 'react';

import type { CustomDetailCellRendererProps } from 'ag-grid-react';
import { CirclePlus, Pencil } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import {
  defaultGradingRows,
  type GradingGridRow,
  GradingMatrix,
  gridToStageGradeCount,
  StatusBadge,
  sumGradingRows,
} from '@/components/common';
import { type ColumnDef, DataTable } from '@/components/common/data-table';
import { Button } from '@/components/ui/button';
import { toast } from '@/components/ui/toast';
import {
  type OnFarmImplantFreshEmbryo,
  OnFarmImplantRequestError,
  toRecordKey,
  useAddOnFarmImplantQueue,
  useCreateOnFarmImplantFreshEmbryos,
} from '@/services/on-farm-implant';

import type { FreshMatingRow } from './columnDefs';
import { EditFreshEmbryosDialog } from './EditFreshEmbryosDialog';
import { useFreshTab } from './FreshTabContext';
import { MatingAllocationTable } from './MatingAllocationTable';

type GradeFilter = 'all' | '1' | '2' | '3';

const queueStateColors = {
  Loaded: { bg: 'bg-blue-100', text: 'text-blue-700' },
  Implanted: { bg: 'bg-green-100', text: 'text-green-700' },
  Discarded: { bg: 'bg-red-100', text: 'text-red-700' },
};

const emptyGradingRows = () => defaultGradingRows.map((row) => ({ ...row }));

const toMessage = (error: unknown, fallback: string) =>
  error instanceof OnFarmImplantRequestError && error.problems.length
    ? error.problems.join(' ')
    : fallback;

export const FreshMatingDetail = ({
  data: mating,
}: CustomDetailCellRendererProps<FreshMatingRow>) => {
  const { t } = useTranslation();
  const {
    sessionId,
    readOnly,
    embryos,
    nextStrawNumber,
    setNextStrawNumber,
    gradingByMating,
    setMatingGrading,
  } = useFreshTab();
  const matingKey = mating ? toRecordKey(mating.id, mating.accountId) : '';
  const gradingRows = gradingByMating[matingKey] ?? defaultGradingRows;
  const setGradingRows = (rows: GradingGridRow[]) =>
    setMatingGrading(matingKey, rows);
  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  const [gradeFilter, setGradeFilter] = useState<GradeFilter>('all');
  const [error, setError] = useState<string | null>(null);
  const [editOpen, setEditOpen] = useState(false);
  const { mutateAsync: createEmbryos, isPending: isCreating } =
    useCreateOnFarmImplantFreshEmbryos();
  const { mutateAsync: queueEmbryos, isPending: isQueueing } =
    useAddOnFarmImplantQueue();

  const matingEmbryos = useMemo(
    () =>
      mating
        ? embryos.filter(
            (embryo) =>
              embryo.matingId === mating.id &&
              (embryo.accountId ?? null) === (mating.accountId ?? null),
          )
        : [],
    [embryos, mating],
  );
  const filteredEmbryos = useMemo(
    () =>
      matingEmbryos.filter(
        (embryo) =>
          gradeFilter === 'all' || embryo.grade === Number(gradeFilter),
      ),
    [matingEmbryos, gradeFilter],
  );
  const loadedEmbryos = matingEmbryos.filter(
    (embryo) => embryo.queueState === 'Loaded',
  );
  const selectableIds = useMemo(
    () =>
      readOnly ? [] : filteredEmbryos.map((embryo) => embryo.sessionEmbryoId),
    [filteredEmbryos, readOnly],
  );
  const selectedLoadedEmbryos = loadedEmbryos.filter((embryo) =>
    selectedIds.includes(embryo.sessionEmbryoId),
  );
  const allSelected =
    selectableIds.length > 0 &&
    selectableIds.every((id) => selectedIds.includes(id));
  const embryoTotal = sumGradingRows(gradingRows);

  const columns = useMemo<ColumnDef<OnFarmImplantFreshEmbryo>[]>(
    () => [
      {
        key: 'select',
        width: 36,
        reorderable: false,
        hideable: false,
        chooserLabel: t('onFarmImplant.fresh.select'),
        header: (
          <input
            type="checkbox"
            aria-label={t('onFarmImplant.fresh.selectAll')}
            checked={allSelected}
            disabled={selectableIds.length === 0}
            onChange={(event) =>
              setSelectedIds(event.target.checked ? selectableIds : [])
            }
          />
        ),
        render: (embryo) => (
          <input
            type="checkbox"
            aria-label={t('onFarmImplant.fresh.selectStraw', {
              straw: embryo.strawNumber,
            })}
            checked={selectedIds.includes(embryo.sessionEmbryoId)}
            disabled={readOnly}
            onChange={(event) =>
              setSelectedIds((current) =>
                event.target.checked
                  ? [...current, embryo.sessionEmbryoId]
                  : current.filter((id) => id !== embryo.sessionEmbryoId),
              )
            }
          />
        ),
      },
      {
        key: 'strawNumber',
        header: t('onFarmImplant.fresh.straw'),
        width: 90,
        align: 'right',
        sortable: true,
        render: (embryo) => embryo.strawNumber,
      },
      {
        key: 'stage',
        header: t('onFarmImplant.fresh.stage'),
        width: 70,
        align: 'right',
        sortable: true,
        render: (embryo) => embryo.stage,
      },
      {
        key: 'grade',
        header: t('onFarmImplant.fresh.gradeLabel'),
        width: 70,
        align: 'right',
        sortable: true,
        render: (embryo) => embryo.grade,
      },
      {
        key: 'matingName',
        header: t('onFarmImplant.fresh.mating'),
        width: 140,
        sortable: true,
        render: (embryo) => (
          <span className="block truncate" title={embryo.matingName ?? ''}>
            {embryo.matingName}
          </span>
        ),
      },
      {
        key: 'donorName',
        header: t('onFarmImplant.fresh.donor'),
        width: 140,
        sortable: true,
        render: (embryo) => embryo.donorName,
      },
      {
        key: 'sireName',
        header: t('onFarmImplant.fresh.sire'),
        width: 140,
        sortable: true,
        render: (embryo) => embryo.sireName,
      },
      {
        key: 'accountName',
        header: t('onFarmImplant.fresh.account'),
        width: 220,
        sortable: true,
        render: (embryo) => embryo.accountName,
      },
      {
        key: 'queueState',
        header: t('onFarmImplant.fresh.status'),
        width: 110,
        align: 'right',
        sortable: true,
        render: (embryo) => (
          <StatusBadge label={embryo.queueState} colorMap={queueStateColors} />
        ),
      },
    ],
    [t, allSelected, selectableIds, selectedIds, readOnly],
  );

  if (!mating) return null;

  const handleCreate = async () => {
    if (!sessionId || readOnly) return;
    if (!nextStrawNumber) {
      setError(t('onFarmImplant.fresh.strawRequired'));
      return;
    }
    if (embryoTotal === 0) {
      setError(t('onFarmImplant.fresh.countRequired'));
      return;
    }

    try {
      await createEmbryos({
        sessionId,
        input: {
          matingId: mating.id,
          accountId: mating.accountId,
          startingStrawNumber: Number(nextStrawNumber),
          entries: gridToStageGradeCount(gradingRows),
        },
      });
      setNextStrawNumber(String(Number(nextStrawNumber) + embryoTotal));
      setGradingRows(emptyGradingRows());
      setError(null);
    } catch (createError) {
      setError(toMessage(createError, t('onFarmImplant.fresh.createFailed')));
    }
  };

  const handleQueue = async (addAll: boolean) => {
    if (!sessionId || readOnly) return;
    try {
      const embryosToQueue = addAll ? loadedEmbryos : selectedLoadedEmbryos;
      const response = await queueEmbryos({
        sessionId,
        sourceType: 'fresh',
        input: {
          items: embryosToQueue.map((embryo) => ({
            sessionEmbryoId: embryo.sessionEmbryoId,
            transferStrawNumber: embryo.strawNumber,
          })),
        },
      });
      setSelectedIds([]);
      toast.add({
        data: { variant: 'success' },
        title: t('onFarmImplant.fresh.queueSuccessTitle'),
        description: addAll
          ? t('onFarmImplant.fresh.queueAllSuccess', {
              count: response.totalQueued,
              mating: mating.recordName ?? mating.id,
            })
          : t('onFarmImplant.fresh.queueSelectedSuccess', {
              count: response.totalQueued,
            }),
      });
    } catch (queueError) {
      toast.add({
        data: { variant: 'error' },
        title: t('onFarmImplant.fresh.queueFailed'),
        description: toMessage(queueError, ''),
      });
    }
  };

  return (
    <div className="bg-background flex flex-col gap-3 p-3">
      <MatingAllocationTable
        matingKey={matingKey}
        matingName={mating.recordName ?? mating.id}
        allocation={mating.allocation}
      />
      <div className="flex max-w-2xl flex-col gap-2">
        <GradingMatrix
          data={gradingRows}
          title=""
          fullWidth
          disabled={readOnly}
          onChange={(rows) => {
            setGradingRows(rows);
            setError(null);
          }}
          onClear={() => setGradingRows(emptyGradingRows())}
        />
        <div className="flex flex-wrap items-center gap-3">
          <Button
            size="sm"
            disabled={readOnly || isCreating}
            onClick={() => void handleCreate()}
          >
            <CirclePlus className="size-3.5" />
            {t('onFarmImplant.fresh.createEmbryos', { count: embryoTotal })}
          </Button>
          {error && (
            <p role="alert" className="text-destructive text-xs">
              {error}
            </p>
          )}
        </div>
      </div>

      <div className="flex flex-col gap-1">
        <div className="flex flex-wrap items-center gap-2 px-1">
          <h3 className="text-xs font-semibold text-gray-600">
            {t('onFarmImplant.fresh.createdEmbryos')} ({matingEmbryos.length})
          </h3>
          <select
            aria-label={t('onFarmImplant.fresh.filterByGrade')}
            value={gradeFilter}
            onChange={(event) =>
              setGradeFilter(event.target.value as GradeFilter)
            }
            className="border-input bg-background h-6 rounded-md border px-1.5 text-xs"
          >
            <option value="all">{t('onFarmImplant.fresh.allGrades')}</option>
            <option value="1">
              {t('onFarmImplant.fresh.grade', { grade: 1 })}
            </option>
            <option value="2">
              {t('onFarmImplant.fresh.grade', { grade: 2 })}
            </option>
            <option value="3">
              {t('onFarmImplant.fresh.grade', { grade: 3 })}
            </option>
          </select>
          <div className="ml-auto flex items-center gap-2">
            {selectedIds.length > 0 && (
              <Button
                variant="ghost"
                size="xs"
                onClick={() => setSelectedIds([])}
              >
                {t('onFarmImplant.fresh.clearSelection')}
              </Button>
            )}
            <Button
              variant="outline"
              size="xs"
              disabled={readOnly || selectedIds.length === 0}
              onClick={() => setEditOpen(true)}
            >
              <Pencil className="size-3" />
              {t('onFarmImplant.fresh.editSelected', {
                count: selectedIds.length,
              })}
            </Button>
            <Button
              variant="outline"
              size="xs"
              disabled={
                readOnly || selectedLoadedEmbryos.length === 0 || isQueueing
              }
              onClick={() => void handleQueue(false)}
            >
              {t('onFarmImplant.fresh.addSelectedToQueue', {
                count: selectedLoadedEmbryos.length,
              })}
            </Button>
            <Button
              size="xs"
              disabled={readOnly || loadedEmbryos.length === 0 || isQueueing}
              onClick={() => void handleQueue(true)}
            >
              {t('onFarmImplant.fresh.addAllToQueue')}
            </Button>
          </div>
        </div>
        <DataTable
          rows={filteredEmbryos}
          columns={columns}
          getRowId={(embryo) => embryo.sessionEmbryoId}
          storageKey="on-farm-implant-fresh-embryo-table"
          reorderable
          columnChooser
          headerClassName="font-['Poppins',sans-serif] font-medium tracking-normal normal-case"
          fillWidth
          emptyMessage={t(
            matingEmbryos.length > 0
              ? 'onFarmImplant.fresh.noEmbryosForGrade'
              : 'onFarmImplant.fresh.noCreatedEmbryos',
          )}
        />
      </div>
      {sessionId && (
        <EditFreshEmbryosDialog
          open={editOpen}
          onOpenChange={setEditOpen}
          sessionId={sessionId}
          sessionEmbryoIds={selectedIds}
          onSaved={(count, nextStraw) => {
            setSelectedIds([]);
            if (nextStraw !== undefined) setNextStrawNumber(String(nextStraw));
            toast.add({
              data: { variant: 'success' },
              title: t('onFarmImplant.fresh.edit.successTitle'),
              description: t('onFarmImplant.fresh.edit.success', { count }),
            });
          }}
        />
      )}
    </div>
  );
};
